import { useState } from "react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import { usePostHogSettings, useSaveSettings } from "../api";

/**
 * Superuser-only key entry. Mirrors the AI Settings page: values are written
 * to the Fernet-encrypted core.app_setting store, so rotating a key takes
 * effect immediately with no rebuild or redeploy.
 *
 * The project key is shown back because it's public by construction (it ships
 * in the browser bundle) and seeing it is how you confirm which PostHog
 * project you're pointed at. The personal key never comes back from the
 * server — only whether one is configured.
 */
export function SettingsTab() {
  const { data, isLoading } = usePostHogSettings(true);
  const save = useSaveSettings();

  const [projectKey, setProjectKey] = useState("");
  const [personalKey, setPersonalKey] = useState("");
  const [projectId, setProjectId] = useState("");

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading settings…</div>;
  }
  if (!data) return null;

  const submit = (patch: Record<string, unknown>) =>
    save.mutate(patch, {
      onSuccess: () => {
        toast.success("PostHog settings saved");
        setProjectKey("");
        setPersonalKey("");
        setProjectId("");
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : "Couldn't save settings"),
    });

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!data.secret_store_ready ? (
        <Alert variant="warning">
          <AlertTitle>Encrypted store not configured</AlertTitle>
          <AlertDescription>
            Set <code>CINCH_OPS_SETTINGS_SECRET</code> on the server to save keys
            in-app. Until then, keys can only be provided via the server environment.
          </AlertDescription>
        </Alert>
      ) : null}

      <Card className="flex flex-col gap-4 p-5">
        <div>
          <h3 className="font-semibold">Capture</h3>
          <p className="text-sm text-muted-foreground">
            The project key the browser sends events with. Public by design — it ships
            inside the SPA bundle.
          </p>
        </div>

        <div className="flex items-center justify-between gap-4 rounded-md bg-muted/40 px-3 py-2">
          <div>
            <div className="text-sm font-medium">Capture enabled</div>
            <div className="text-xs text-muted-foreground">
              Off stops the SDK loading entirely, without dropping the key.
            </div>
          </div>
          <Switch
            checked={data.capture_enabled}
            onCheckedChange={(v) => submit({ capture_enabled: v })}
            disabled={save.isPending}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ph-project-key">Project API key</Label>
          <div className="flex gap-2">
            <Input
              id="ph-project-key"
              value={projectKey}
              onChange={(e) => setProjectKey(e.target.value)}
              placeholder={data.project_key ?? "phc_…"}
              disabled={!data.secret_store_ready}
            />
            <Button
              onClick={() => submit({ project_key: projectKey })}
              disabled={!projectKey || save.isPending || !data.secret_store_ready}
            >
              Save
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {data.project_key_configured
              ? `Configured (from ${data.project_key_source}).`
              : "Not configured — analytics is off."}{" "}
            Project settings → Project variables in PostHog.
          </p>
        </div>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <div>
          <h3 className="font-semibold">Read-back</h3>
          <p className="text-sm text-muted-foreground">
            Needed for this dashboard and the weekly review. Not needed to capture
            events.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ph-project-id">Project ID</Label>
          <div className="flex gap-2">
            <Input
              id="ph-project-id"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              placeholder={data.project_id ?? "e.g. 553668"}
              disabled={!data.secret_store_ready}
            />
            <Button
              onClick={() => submit({ project_id: projectId })}
              disabled={!projectId || save.isPending || !data.secret_store_ready}
            >
              Save
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            The numeric id in your PostHog URL — the Query API path segment.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ph-personal-key">Personal API key</Label>
          <div className="flex gap-2">
            <Input
              id="ph-personal-key"
              type="password"
              value={personalKey}
              onChange={(e) => setPersonalKey(e.target.value)}
              placeholder={data.personal_key_configured ? "•••••••• (configured)" : "phx_…"}
              disabled={!data.secret_store_ready}
            />
            <Button
              onClick={() => submit({ personal_api_key: personalKey })}
              disabled={!personalKey || save.isPending || !data.secret_store_ready}
            >
              Save
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {data.personal_key_configured
              ? `Configured (from ${data.personal_key_source}). `
              : "Not configured. "}
            Create at{" "}
            <a
              className="text-power underline underline-offset-2"
              href="https://us.posthog.com/settings/user-api-keys"
              target="_blank"
              rel="noreferrer"
            >
              us.posthog.com/settings/user-api-keys
            </a>{" "}
            with the <code className="rounded bg-muted px-1">query:read</code> scope.
            This one is a real secret — it reads all your analytics back, and it is
            never displayed again after saving.
          </p>
        </div>

        <div
          className={`rounded-md px-3 py-2 text-sm ${
            data.read_back_ready
              ? "bg-success/10 text-success"
              : "bg-muted/40 text-muted-foreground"
          }`}
        >
          {data.read_back_ready
            ? "Read-back is configured — the Overview tab will load."
            : "Read-back needs both the project id and a personal API key."}
        </div>
      </Card>
    </div>
  );
}
